import { useCallback, useMemo } from 'react';
import copyIcon from '@/pages/aigc/assets/svg/agent-table-copy.svg';
import copyActiveIcon from '@/pages/aigc/assets/svg/agent-table-copy-active.svg';
import downloadIcon from '@/pages/aigc/assets/svg/agent-table-download.svg';
import downloadActiveIcon from '@/pages/aigc/assets/svg/agent-table-download-active.svg';
import {
  extractTableData,
  copyTableToClipboard,
  downloadTableAsExcel,
  findPreviousElementTitle,
} from './utils/index';
import type { ITableActionConfig, ITableNode } from './types';
import { useAgentStoreContext } from '../../hooks/useLocalState';
import { useSessionId } from '../../hooks/useAgentModalSata';
import { formatChartTaleDta, formatData } from '../MainContent/_utils';
import { handleDownload } from '../DownloadLink/_utils';

export interface IUseTableActionsOptions {
  /** 表格 AST 节点 */
  node?: ITableNode;
  /** 表格唯一标识符 */
  tableId?: string;
  /** 答案容器选择器 */
  answerContainer?: string;
  /** 消息ID */
  messageId: string;
  /** 答案 */
  answer: any[];
}

/**
 * 表格操作 Hook
 * @description 封装表格复制和下载的默认操作逻辑
 */
export const useTableActions = ({
  node,
  tableId,
  answerContainer,
  messageId,
  answer
}: IUseTableActionsOptions): ITableActionConfig[] => {
  const { getItem: agentSetItem } = useAgentStoreContext();
  const { sessionId: routeSessionId } = useSessionId();

  /**
   * 复制表格到剪贴板（支持 Excel 和 Word 文档）
   */
  const handleCopyTable = useCallback(async () => {
    if (!node) {
      console.warn('表格节点不存在');
      return false;
    }
    const tableData = extractTableData(node);
    if (!tableData || tableData.length === 0) {
      console.warn('表格数据为空');
      return false;
    }
    const success = await copyTableToClipboard(tableData);
    return success;
  }, [node]);
  // 监听这个状态

  /**
   * 多 tid 合并下载
   * @description 将多个 tid 对应的表格数据合并到一个 Excel 文件的多个 sheet 中
   */
  const handleDownloadWithMultipleTids = useCallback(
    async (tids: Array<{ tableId: string; tidContent: string; title: string; }>) => {
      const conversations = agentSetItem(
        `__dba_chat_history_${routeSessionId}__`
      );

      // 1. 找到当前消息
      const currentMessage = Array.isArray(conversations)
        ? conversations.find((item: any) => item?.messageId === messageId)
        : undefined;

      // 2. 从 answer 中找出 type 为 biData 的项
      const answerList = currentMessage?.answer || answer;
      const biDataItem = Array.isArray(answerList)
        ? answerList.find((item: any) => item?.type === 'biData')
        : undefined;

      // 3. 获取 biData 的 value 数组
      const biDataValue = biDataItem?.value;
      
      if (!biDataValue || !Array.isArray(biDataValue)) {
        console.warn('[handleDownloadWithMultipleTids] 未找到 biData');
        return;
      }
      
      // 4. 收集所有 tid 对应的数据
      const allFormattedData: Array<{
        sheetName: string;
        formattedData: any;
        rawData: any[];
      }> = [];
      
      for (const tid of tids) {
        // 查找对应的 biData (使用 trim 去除可能的空格)
        const targetData = biDataValue.find(
          (item: any) => item?.data_id?.trim() === tid.tidContent?.trim()
        );
        
        if (!targetData) {
          console.warn(`[handleDownloadWithMultipleTids] 未找到 tid: ${tid.tidContent}`);
          continue;
        }
        
        // 格式化数据（复用原有逻辑）
        const formattedData = formatChartTaleDta(formatData([targetData]));
        
        if (!formattedData) {
          console.warn(`[handleDownloadWithMultipleTids] 格式化失败: ${tid.tidContent}`);
          continue;
        }
        
        allFormattedData.push({
          sheetName: tid.title,
          formattedData,
          rawData: formattedData.data || [],
        });
      }
      
      if (allFormattedData.length === 0) {
        console.warn('[handleDownloadWithMultipleTids] 没有可下载的数据');
        return;
      }
      
      // 5. 生成文件名（使用第一个 tid 的标题，或默认名称）
      const fileName = allFormattedData[0].sheetName || '数据导出';
      
      // 6. 调用多 sheet 下载
      try {
        await handleDownload({
          multipleSources: allFormattedData,
          fileName,
          enableNotification: false,
        } as any);
      } catch (error) {
        console.error('[handleDownloadWithMultipleTids] 下载失败:', error);
      }
    },
    [answerContainer, messageId, agentSetItem, routeSessionId],
  );

  /**
   * 单 tid 下载（带 biData 查找）
   * @description 从 biData 中找到匹配的数据，使用 DownloadLink 的下载逻辑
   */
  const handleDownloadWithTid = useCallback(
    async (
      tableData: string[][],
      tid: string,
      tidContent?: string,
      title?: string,
    ) => {
      const conversations = agentSetItem(
        `__dba_chat_history_${routeSessionId}__`,
      );

      // 1. 找到当前消息
      const currentMessage = Array.isArray(conversations)
        ? conversations.find((item: any) => item?.messageId === messageId)
        : undefined;

      // 2. 从 answer 中找出 type 为 biData 的项
      const answerList = currentMessage?.answer || answer;
      const biDataItem = Array.isArray(answerList)
        ? answerList.find((item: any) => item?.type === 'biData')
        : undefined;

      // 3. 从 biData 的 value 数组中找出 data_id 等于 tidContent 的数据
      const biDataValue = biDataItem?.value;
      const targetData = Array.isArray(biDataValue)
        ? biDataValue.find((item: any) => item?.data_id === tidContent)
        : undefined;

      if (!targetData) {
        console.warn('[handleDownloadWithTid] 未找到匹配的 biData 数据', {
          tidContent,
        });
        // 降级使用默认下载方式
        await downloadTableAsExcel(tableData, tid, answerContainer, title);
        return;
      }

      try {
        // 4. 格式化数据（复用 ChartAdapter 的逻辑）
        const formattedData = formatChartTaleDta(formatData([targetData]));

        // 5. 检查格式化后的数据是否有效
        if (!formattedData) {
          console.warn('[handleDownloadWithTid] 格式化数据失败');
          await downloadTableAsExcel(tableData, tid, answerContainer, title);
          return;
        }

        // 6. 生成文件名
        const fileName = title || formattedData?.title || 'download';

        // 7. 调用 handleDownload（复用 DownloadLink 的下载逻辑）
        await handleDownload({
          formattedData: {
            xAxis: formattedData?.xAxis,
            legends: formattedData?.legends,
            filter_info: formattedData?.filter_info,
            dimension_info: formattedData?.dimension_info,
            metrics_info: formattedData?.metrics_info,
            data: formattedData?.data,
            title: formattedData?.title,
          },
          rawData: formattedData?.data,
          downloadTitle: fileName,
          filterVal: {},
          fileName,
          enableNotification: false,
        });

        console.log('[handleDownloadWithTid] 下载成功', {
          tidContent,
          fileName,
        });
      } catch (error) {
        console.error('[handleDownloadWithTid] 下载失败:', error);
        // 降级使用默认下载方式
        await downloadTableAsExcel(tableData, tid, answerContainer, title);
      }
    },
    [answerContainer, messageId, agentSetItem, routeSessionId],
  );

  /**
   * 下载表格为Excel文件（智能模式）
   * @description 
   * - 自动检测是单个 tid 还是多个 tid
   * - 多个 tid：合并下载到一个文件的多个 sheet
   * - 单个 tid：使用 biData 下载
   * - 无 tid：使用默认下载方式
   */
  const handleDownloadTable = useCallback(async () => {
    if (!node) {
      console.warn('表格节点不存在');
      return false;
    }
    if (!tableId) {
      console.warn('表格ID不存在');
      return false;
    }
    
    try {
      // 1. 智能检测 tid（自动判断单个还是多个）
      const result = findPreviousElementTitle(tableId);
      
      // 2. 多 tid 场景：合并下载
      if (result.isMultiple && result.tids.length > 1) {
        await handleDownloadWithMultipleTids(result.tids);
        return true;
      }
      
      // 3. 单 tid 场景：使用 biData 下载
      if (result.hasTid && result.tids.length === 1) {
        const tableData = extractTableData(node);
        const tid = result.tids[0];
        
        await handleDownloadWithTid(
          tableData,
          tableId,
          tid.tidContent,
          tid.title
        );
        return true;
      }
      
      // 4. 无 tid 场景：默认下载
      const tableData = extractTableData(node);
      await downloadTableAsExcel(tableData, tableId, answerContainer, '');
      return true;
      
    } catch (error) {
      console.error('[handleDownloadTable] 下载失败:', error);
      return false;
    }
  }, [
    node, 
    tableId, 
    answerContainer, 
    handleDownloadWithTid, 
    handleDownloadWithMultipleTids
  ]);

  /**
   * 表格操作配置列表
   */
  const tableActionConfigs: ITableActionConfig[] = useMemo(
    () => [
      {
        type: 'copy',
        icon: copyIcon,
        activeIcon: copyActiveIcon,
        tooltipKey: 'dba_table_copy',
        successTooltipKey: 'dba_common_copy_success',
        autoHideTooltip: true,
        onClick: handleCopyTable,
      },
      {
        type: 'download',
        icon: downloadIcon,
        activeIcon: downloadActiveIcon,
        tooltipKey: 'dba_table_download',
        autoHideTooltip: false,
        onClick: handleDownloadTable,
      },
    ],
    [handleCopyTable, handleDownloadTable],
  );

  return tableActionConfigs;
};

export default useTableActions;
